import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * useDebouncedSearch - Industry-standard two-state search pattern.
 * 
 * Returns:
 *   searchInput     – immediate typed value (bind to input)
 *   setSearchInput  – setter for immediate value
 *   debouncedValue  – debounced value (use for filtering / API calls)
 *   setDebouncedValue – force-set debounced value (e.g., URL init)
 *   clearSearch     – resets both states
 *
 * @param {string} initialValue - initial search value
 * @param {number} delay - debounce delay in ms (default 350)
 */
export function useDebouncedSearch(initialValue = '', delay = 350) {
  const [searchInput, setSearchInput] = useState(initialValue);
  const [debouncedValue, setDebouncedValue] = useState(initialValue);
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      setDebouncedValue(searchInput);
    }, delay);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [searchInput, delay]);

  const clearSearch = useCallback(() => {
    setSearchInput('');
    setDebouncedValue('');
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return { searchInput, setSearchInput, debouncedValue, setDebouncedValue, clearSearch };
}

export default useDebouncedSearch;
